/**
 * Call-to-action attached to each onboarding journey step.
 *
 * The API only ever returns step keys — routes are a client concern, so they are resolved
 * here.
 *
 * Steps whose target is a piece of learning material (intro video, MOOC players, quiz,
 * webinar cards, downloadable resources) link to the Resources tab with `?block=<id>`, which
 * opens that block and scrolls to it: the journey reorganises that content around a path, it
 * does not replace it. Steps that hand out a document therefore point at the resources block
 * rather than downloading a file straight from the checklist — the block shows every format
 * and description, which a bare CTA cannot.
 */
export type HelpResourceBlock =
  | 'introVideo'
  | 'moocs'
  | 'quiz'
  | 'webinars'
  | 'resources';

export interface JourneyStepAction {
  route: string;
  queryParams?: Record<string, string>;
}

/** The three tabs of /home/objectives, deep-linked through the `tab` query param. */
const OBJECTIVES_ROUTE = '/home/objectives';

/** The content blocks of /home/help/resources, deep-linked through the `block` query param. */
const HELP_RESOURCES_ROUTE = '/home/help/resources';

const resourceBlock = (block: HelpResourceBlock): JourneyStepAction => ({
  route: HELP_RESOURCES_ROUTE,
  queryParams: { block },
});

/** A step with no entry here simply gets no call to action. */
export const JOURNEY_STEP_ACTIONS: Record<
  string,
  JourneyStepAction | undefined
> = {
  // 1. Découvrir
  watch_intro_video: resourceBlock('introVideo'),
  create_first_trip: { route: '/home/travel-board' },
  register_onboarding_webinar: resourceBlock('webinars'),

  // 2. Comprendre
  complete_quiz: resourceBlock('quiz'),
  watch_moocs: resourceBlock('moocs'),
  explore_how_it_works: { route: '/home/help/how-it-works' },
  set_reduction_objectives: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'target' },
  },
  select_commitments: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'actions' },
  },
  select_climate_risks: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'risks' },
  },

  // 3. Diminuer
  import_all_trips: { route: '/home/travel-board' },
  apply_reduction_tips: { route: '/home/travel-board' },
  define_carbon_commitments: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'actions' },
  },

  // 4. Sensibiliser
  invite_teammate: { route: '/home/account/users' },
  share_awareness_kit: resourceBlock('resources'),
  present_case_study: resourceBlock('resources'),
  share_objectives_with_team: { route: OBJECTIVES_ROUTE },
  // `communicate_on_social_media` has no entry: the user posts on their own channels.

  // 5. Faire le point
  review_objectives_progress: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'target' },
  },
  register_whats_next_webinar: resourceBlock('webinars'),
  present_whats_next: resourceBlock('resources'),
  set_more_ambitious_objectives: {
    route: OBJECTIVES_ROUTE,
    queryParams: { tab: 'target' },
  },
};
